/**
 * Tasks – what the owner carries out (`?view=todo|history`):
 * - To do (default) – every open task the owner does: what the AI team needs
 *   from them (Done…/Decline… feed the outcome back into the proposing
 *   employee's queue) and their own tasks. From `GET /tasks/queue`, grouped –
 *   a campaign bundle's tasks, other batches, the owner's own tasks, then
 *   LinkedIn messages · calls · other, the team requests folded at the end
 *   (`lib/taskGroups.ts`); `?work=<group key>` works one group through, one
 *   task at a time (`components/tasks/WorkThrough.tsx`).
 * - History – closed tasks, newest close first, with their outcome.
 *
 * A group with a key (a campaign bundle or a seat's batch) takes one note for
 * all its tasks: "Request changes…" sends them back to the seat, which
 * revises each in place (`components/tasks/RequestChangesModal.tsx`). Until
 * then they stay here, marked "being revised"; "Take back" ends the round.
 *
 * A click on any row opens the task drawer (`?task=<id>`,
 * `components/tasks/TaskDrawer.tsx`). Tasks an AI employee executes itself
 * have no list here – their jobs live in the employee drawer on /team.
 */
import { useMemo, useState, type MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";

import { humanTaskOf, tasksApi, useTaskHistory, useTaskQueue, type HumanTask } from "../api/tasks";
import type { Task } from "../types";
import { Age } from "../components/Age";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { PriorityBadge } from "../components/PriorityBadge";
import { QueryBoundary } from "../components/QueryBoundary";
import { TaskOutcomeModal } from "../components/TaskOutcomeModal";
import { RequestChangesModal, type RequestChangesTarget } from "../components/tasks/RequestChangesModal";
import { TaskDrawer } from "../components/tasks/TaskDrawer";
import { useToast } from "../components/toast/context";
import { WorkThrough } from "../components/tasks/WorkThrough";
import { ageSeconds, berlinDateTime } from "../lib/console";
import {
  findGroup,
  groupCampaignHref,
  groupProgressText,
  groupTasks,
  isBeingRevised,
  taskTypeLabel,
  taskWho,
  type TaskGroup,
} from "../lib/taskGroups";

type TaskView = "todo" | "history";

const VIEWS: { value: TaskView; label: string }[] = [
  { value: "todo", label: "To do" },
  { value: "history", label: "History" },
];

/** A group with more tasks than this starts folded. */
const FOLD_ABOVE = 5;

/** How a closed task ended, for the History list. */
function outcomeOf(task: Task): { label: string; tone: "ok" | "muted" } {
  const d = task.data;
  if (d.withdrawal) return { label: "withdrawn", tone: "muted" };
  if (d.closure?.kind === "expired") return { label: "expired", tone: "muted" };
  if (d.status === "completed") {
    return { label: d.closure?.kind === "auto_detected" ? "sent" : "done", tone: "ok" };
  }
  return { label: "declined", tone: "muted" };
}

/** Clicks on a row's own buttons and links don't open the drawer. */
function fromControl(e: MouseEvent): boolean {
  return !!(e.target as HTMLElement).closest("a, button, input, textarea, select, summary");
}

/** One open task of a group: who, what, from whom, its links, age, quick close. */
function QueueRow({
  task,
  nowMs,
  taskHref,
  onOpen,
  onOutcome,
}: {
  task: HumanTask;
  nowMs: number;
  taskHref: string;
  onOpen: () => void;
  onOutcome: (task: HumanTask) => void;
}) {
  const link = task.linkedin_url ?? task.action_url;
  return (
    <tr className="align-top cursor-pointer hover:bg-surface-tint" onClick={(e) => !fromControl(e) && onOpen()}>
      <td>
        <div className="font-heading font-semibold text-ink">
          <Link to={taskHref} className="link">
            {taskWho(task)}
          </Link>
          {task.company_name ? <span className="text-sm font-normal text-ink-low"> · {task.company_name}</span> : null}
        </div>
        {task.title && task.contact_name ? <div className="text-sm text-ink-medium">{task.title}</div> : null}
        {task.agent_error ? <div className="text-xs text-danger">{task.agent_error}</div> : null}
        {isBeingRevised(task) ? (
          <div className="text-xs text-ink-low">
            being revised by <span className="font-mono">{task.revision?.seat}</span>
          </div>
        ) : task.revision?.released ? (
          <div className="text-xs text-warn-fg">not revised</div>
        ) : null}
      </td>
      <td className="text-sm text-ink-medium whitespace-nowrap">
        {taskTypeLabel(task.task_type)}
        {task.priority === "high" ? <span className="ml-1.5"><PriorityBadge priority={1} /></span> : null}
      </td>
      <td className="whitespace-nowrap">
        {task.agent_slug ? <span className="pill pill--signal">{task.agent_slug}</span> : <span className="text-sm text-ink-low">you</span>}
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" className="ml-1.5 text-sm text-ink-low" aria-label="Open external link" title={link}>
            ↗
          </a>
        ) : null}
        {task.message_url ? (
          <a
            href={task.message_url}
            target="_blank"
            rel="noreferrer"
            className="ml-1.5 text-sm text-ink-low"
            aria-label="Open LinkedIn messaging"
            title="Open LinkedIn messaging"
          >
            ✉
          </a>
        ) : null}
      </td>
      <td className="whitespace-nowrap text-right">
        <Age seconds={ageSeconds(task.created_at, nowMs)} />
        {task.expires_at ? (
          <div className="text-xs text-ink-low font-mono" title="Expires">
            ⏳ {berlinDateTime(task.expires_at)}
          </div>
        ) : null}
      </td>
      <td className="text-right space-x-1.5 whitespace-nowrap">
        <button className="btn btn-main btn-sm" onClick={() => onOutcome(task)}>
          Done…
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => onOutcome(task)}>
          Decline…
        </button>
      </td>
    </tr>
  );
}

/** One group as a parent row – label, from, count, oldest, progress – that
 * unfolds into its tasks. */
function GroupPanel({
  group,
  nowMs,
  workHref,
  taskHref,
  onOpen,
  onOutcome,
  onRequestChanges,
  onTakeBack,
}: {
  group: TaskGroup;
  nowMs: number;
  workHref: string;
  taskHref: (id: string) => string;
  onOpen: (id: string) => void;
  onOutcome: (task: HumanTask) => void;
  onRequestChanges: (target: RequestChangesTarget) => void;
  onTakeBack: (roundId: string) => void;
}) {
  const [open, setOpen] = useState(group.tasks.length <= FOLD_ABOVE);
  const campaignHref = groupCampaignHref(group);
  const seats = [...new Set(group.tasks.map((t) => t.agent_slug).filter((slug): slug is string => !!slug))];
  const oldest = group.tasks.reduce<string | null>(
    (min, t) => (t.created_at && (!min || t.created_at < min) ? t.created_at : min),
    null,
  );
  // One note for the whole wave – only a group with a key, and only for the
  // tasks not already with the seat.
  const sendable = group.tasks.filter((t) => t.agent_id && !isBeingRevised(t));
  return (
    <div className="panel panel--flush overflow-x-auto mb-4">
      <div className="panel-header">
        <div className="min-w-0">
          <h2 className="truncate">
            <button type="button" className="text-left" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
              <span className="text-ink-low font-mono mr-1.5">{open ? "▾" : "▸"}</span>
              {group.label} <span className="text-ink-low font-normal tnum">· {group.tasks.length}</span>
            </button>
          </h2>
          <div className="text-xs text-ink-low">
            {seats.length ? <>from <span className="font-mono">{seats.join(", ")}</span> · </> : null}
            {groupProgressText(group)}
            {oldest ? <> · oldest <Age seconds={ageSeconds(oldest, nowMs)} /></> : null}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {campaignHref ? (
            <Link to={campaignHref} className="link-quiet text-xs">
              campaign →
            </Link>
          ) : null}
          {group.type === "batch" && sendable.length ? (
            <button
              type="button"
              className="btn btn-second btn-sm"
              onClick={() =>
                onRequestChanges({ groupKey: group.key, label: group.label, count: sendable.length, seats })
              }
            >
              Request changes…
            </button>
          ) : null}
          <Link to={workHref} className="btn btn-main btn-sm">
            Work through · {group.tasks.length}
          </Link>
        </div>
      </div>
      {group.revising.map((round) => (
        <div key={round.roundId} className="callout callout--info text-sm mx-4 mb-3 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            {round.count} {round.count === 1 ? "task" : "tasks"} being revised by{" "}
            <span className="font-mono">{round.seat ?? "the seat"}</span>
            {round.since ? <> since <span className="font-mono">{berlinDateTime(round.since)}</span></> : null}
            <div className="text-ink-medium whitespace-pre-wrap">Your note: {round.note}</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm shrink-0" onClick={() => onTakeBack(round.roundId)}>
            Take back
          </button>
        </div>
      ))}
      {open ? (
        <table className="table">
          <tbody>
            {group.tasks.map((t) => (
              <QueueRow
                key={t.task_id}
                task={t}
                nowMs={nowMs}
                taskHref={taskHref(t.task_id)}
                onOpen={() => onOpen(t.task_id)}
                onOutcome={onOutcome}
              />
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function HistoryRow({ task, taskHref, onOpen }: { task: Task; taskHref: string; onOpen: () => void }) {
  const d = task.data;
  const item = humanTaskOf(task);
  const outcome = outcomeOf(task);
  const reason = d.withdrawal?.reason ?? d.decline_reason ?? d.feedback;
  const closedAt = d.closure?.at ?? d.withdrawal?.at ?? task.updated_at;
  return (
    <tr className="align-top cursor-pointer hover:bg-surface-tint" onClick={(e) => !fromControl(e) && onOpen()}>
      <td className="whitespace-nowrap font-mono text-xs text-ink-low pt-3">{berlinDateTime(closedAt)}</td>
      <td>
        <Link to={taskHref} className="link font-heading font-semibold">
          {taskWho(item)}
        </Link>
        {item.title && item.contact_name ? <div className="text-sm text-ink-medium">{item.title}</div> : null}
        {reason ? <div className="text-xs text-ink-low mt-0.5 line-clamp-2">{String(reason)}</div> : null}
      </td>
      <td className="text-sm text-ink-medium whitespace-nowrap">{taskTypeLabel(d.task_type)}</td>
      <td className="whitespace-nowrap">
        {d.proposed_by_agent_slug ? (
          <span className="pill pill--signal">{d.proposed_by_agent_slug}</span>
        ) : (
          <span className="text-sm text-ink-low">you</span>
        )}
      </td>
      <td className="text-right">
        <Pill value={outcome.label} tone={outcome.tone} />
      </td>
    </tr>
  );
}

export function TasksPage() {
  const [sp, setSp] = useSearchParams();
  const view: TaskView = sp.get("view") === "history" ? "history" : "todo";
  const work = sp.get("work") || "";
  const openTaskId = sp.get("task");
  // Proposer filter – a slug, matched client-side (the select is built from
  // the loaded rows; /tasks is not admin-gated, so no agent roster).
  const agentSlug = sp.get("agent") || "";
  const [nowMs] = useState(() => Date.now());
  const [outcomeTask, setOutcomeTask] = useState<HumanTask | null>(null);
  const [changesTarget, setChangesTarget] = useState<RequestChangesTarget | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const queue = useTaskQueue();
  const history = useTaskHistory();

  const queueItems = useMemo(
    () => (queue.data?.items ?? []).filter((t) => !agentSlug || t.agent_slug === agentSlug),
    [queue.data, agentSlug],
  );
  const historyRows = useMemo(
    () => (history.data ?? []).filter((t) => !agentSlug || t.data.proposed_by_agent_slug === agentSlug),
    [history.data, agentSlug],
  );
  const agentSlugs = useMemo(() => {
    const slugs = new Set<string>();
    if (view === "todo") {
      for (const t of queue.data?.items ?? []) if (t.agent_slug) slugs.add(t.agent_slug);
    } else {
      for (const t of history.data ?? []) if (t.data.proposed_by_agent_slug) slugs.add(t.data.proposed_by_agent_slug);
    }
    return [...slugs].sort();
  }, [view, queue.data, history.data]);
  const groups = useMemo(() => groupTasks(queueItems, queue.data?.batches ?? []), [queueItems, queue.data]);

  function withParams(change: (next: URLSearchParams) => void): string {
    const next = new URLSearchParams(sp);
    change(next);
    return `?${next.toString()}`;
  }
  const viewHref = (value: TaskView) =>
    withParams((next) => {
      next.set("view", value);
      next.delete("agent");
      next.delete("work");
    });
  const workHref = (key: string) =>
    withParams((next) => {
      next.set("view", "todo");
      next.set("work", key);
    });
  const taskHref = (id: string) => withParams((next) => next.set("task", id));

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value);
    else next.delete(key);
    setSp(next);
  }
  const openTask = (id: string) => setParam("task", id);

  async function takeBack(roundId: string) {
    try {
      const result = await tasksApi.takeBack(roundId);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["tasks"] }),
        qc.invalidateQueries({ queryKey: ["console"] }),
      ]);
      toast.ok(`Taken back – ${result.cleared} ${result.cleared === 1 ? "task" : "tasks"} no longer with the seat`);
    } catch (err) {
      toast.error(err);
    }
  }
  const drawer = <TaskDrawer taskId={openTaskId} onClose={() => setParam("task", "")} />;

  if (view === "todo" && work) {
    const group = findGroup(queueItems, queue.data?.batches ?? [], work);
    const label =
      group?.label ??
      queue.data?.batches.find((b) => b.key === work)?.label ??
      (work === "team" ? "Team requests" : work === "own" ? "Your own" : work.startsWith("kind:") ? "Tasks" : work);
    return (
      <div className="page">
        <PageHeader title="Tasks" description="One task at a time – the next one appears when you close the current one." />
        {queue.isLoading ? (
          <div className="py-10 text-center text-ink-low text-sm">Loading…</div>
        ) : (
          <WorkThrough key={work} group={group} label={label} nowMs={nowMs} onExit={() => setParam("work", "")} />
        )}
        {drawer}
      </div>
    );
  }

  const count = view === "todo" ? queueItems.length : historyRows.length;

  return (
    <div className="page">
      <PageHeader
        title="Tasks"
        description="What you carry out: what the AI team needs from you, and your own tasks. Click a task to see everything behind it."
      />

      <nav className="subnav" aria-label="Task views">
        {VIEWS.map((v) => (
          <Link key={v.value} to={viewHref(v.value)} className={clsx(view === v.value && "is-active")}>
            {v.label}
          </Link>
        ))}
      </nav>

      <div className="flex items-end gap-4 mb-5 pb-5 border-b border-rule flex-wrap">
        {agentSlugs.length > 0 ? (
          <div>
            <label className="label" htmlFor="task-agent">
              From
            </label>
            <select
              id="task-agent"
              className="input w-[200px]"
              value={agentSlug}
              onChange={(e) => setParam("agent", e.target.value)}
            >
              <option value="">everyone</option>
              {agentSlugs.map((slug) => (
                <option key={slug} value={slug}>
                  {slug}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="ml-auto text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">{count}</span> {view === "todo" ? "open" : "closed"}
        </div>
      </div>

      {view === "todo" ? (
        <QueryBoundary
          query={queue}
          isEmpty={() => queueItems.length === 0}
          empty={
            <div className="empty-state">
              <div className="title">Nothing to do</div>
              <p className="hint">The AI team is not waiting on you, and you have no open tasks.</p>
            </div>
          }
        >
          {() => (
            <>
              {groups.groups.map((g) => (
                <GroupPanel
                  key={g.key}
                  group={g}
                  nowMs={nowMs}
                  workHref={workHref(g.key)}
                  taskHref={taskHref}
                  onOpen={openTask}
                  onOutcome={setOutcomeTask}
                  onRequestChanges={setChangesTarget}
                  onTakeBack={(roundId) => void takeBack(roundId)}
                />
              ))}
              {groups.team.length ? (
                <details className="panel panel--flush">
                  <summary className="panel-header cursor-pointer">
                    <h2>Team requests · {groups.team.length}</h2>
                    <Link to={workHref("team")} className="btn btn-ghost btn-sm" onClick={(e) => e.stopPropagation()}>
                      Work through
                    </Link>
                  </summary>
                  <table className="table">
                    <tbody>
                      {groups.team.map((t) => (
                        <QueueRow
                          key={t.task_id}
                          task={t}
                          nowMs={nowMs}
                          taskHref={taskHref(t.task_id)}
                          onOpen={() => openTask(t.task_id)}
                          onOutcome={setOutcomeTask}
                        />
                      ))}
                    </tbody>
                  </table>
                </details>
              ) : null}
              {queue.data?.truncated ? (
                <p className="text-xs text-ink-low">The list shows the first {queue.data.count} open tasks.</p>
              ) : null}
            </>
          )}
        </QueryBoundary>
      ) : (
        <QueryBoundary
          query={history}
          isEmpty={() => historyRows.length === 0}
          empty={
            <div className="empty-state">
              <div className="title">No closed tasks</div>
              <p className="hint">Tasks you close, decline or that expire show up here.</p>
            </div>
          }
        >
          {() => (
            <div className="panel panel--flush overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Closed</th>
                    <th>Task</th>
                    <th>Type</th>
                    <th>From</th>
                    <th className="text-right">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((t) => (
                    <HistoryRow key={t.id} task={t} taskHref={taskHref(t.id)} onOpen={() => openTask(t.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryBoundary>
      )}

      <TaskOutcomeModal task={outcomeTask} onClose={() => setOutcomeTask(null)} />
      <RequestChangesModal target={changesTarget} onClose={() => setChangesTarget(null)} />
      {drawer}
    </div>
  );
}
